// TODO: https://github.com/combinaut/sparkle/issues/31058 rewrites this into SpFinder and deletes legacy/
/** Private publication bridge from legacy finder transports to the enclosing wrapper. */
import { createElementConnection, observeHostConnections } from "#ce/shared/element-connection";
import { resolveFinder } from "#ce/shared/finder-association";
import { cloneFinderData } from "#ce/components/SpFinder/engine/finder-data";
import { MAP_COMMANDS } from "#ce/components/SpFinder/engine/map-commands";
import { RESULT_COMMANDS } from "#ce/components/SpFinder/engine/result-commands";

const legacyNearEntryKey = "spFinderLegacyNear";
const pendingNearLifetime = 5 * 60 * 1000;

/** Returns the key that identifies a finder across its landing and results pages: its id, root path and results route. */
export function createLegacyNearIdentity(options, elementId) {
  return JSON.stringify([
    options?.uuid || elementId || "",
    String(options?.rootPath ?? window.location.pathname),
    String(options?.routes?.results ?? ""),
  ]);
}

/**
 * Used to carry Near provenance across a same-origin navigation. Near provenance marks a Near location
 * as transient: found by device or IP lookup rather than picked by the visitor, so it is never persisted.
 * It holds no search criteria and no saved location state.
 */
export function createLegacyNearProvenance(identity) {
  const storageKey = `spFinderPendingNear:${identity}`;
  let queued;

  function nearIdentity(params) {
    const zip = params.zip ?? params.near;
    const coordinate = (value, limit) =>
      value != null &&
      String(value).trim() !== "" &&
      Number.isFinite(Number(value)) &&
      Math.abs(Number(value)) <= limit;
    if (coordinate(params.lat, 90) && coordinate(params.lng, 180)) {
      return JSON.stringify([String(zip ?? ""), String(Number(params.lat)), String(Number(params.lng))]);
    }
    return typeof zip === "string" && zip.trim() ? JSON.stringify([zip]) : undefined;
  }

  function entry(params) {
    const value = window.history.state?.[legacyNearEntryKey]?.[identity];
    return typeof value?.near === "string" &&
      value.version === 1 &&
      value.path === window.location.pathname &&
      value.near === nearIdentity(params)
      ? value
      : undefined;
  }

  function writeEntry(params, transient, inheritedState = window.history.state) {
    if (
      !transient &&
      !inheritedState?.[legacyNearEntryKey]?.[identity] &&
      !window.history.state?.[legacyNearEntryKey]?.[identity]
    ) {
      return;
    }
    const current = window.history.state?.[legacyNearEntryKey]?.[identity];
    if (
      transient &&
      current?.version === 1 &&
      current.path === window.location.pathname &&
      current.near === nearIdentity(params)
    ) {
      return;
    }
    const state = { ...inheritedState, ...window.history.state };
    const entries = { ...state[legacyNearEntryKey] };
    if (transient && nearIdentity(params)) {
      entries[identity] = { version: 1, path: window.location.pathname, near: nearIdentity(params) };
    } else {
      delete entries[identity];
    }
    if (Object.keys(entries).length) {
      state[legacyNearEntryKey] = entries;
    } else {
      delete state[legacyNearEntryKey];
    }
    window.history.replaceState(state, "", window.location.href);
  }

  function stage(destination, params, metadata) {
    window.sessionStorage.removeItem(storageKey);
    const url = new URL(destination, window.location.href);
    const near = nearIdentity(params);
    if (url.origin === window.location.origin && near && metadata?.transientLocation === true) {
      window.sessionStorage.setItem(
        storageKey,
        JSON.stringify({
          version: 1,
          destination: url.href,
          near,
          expires: Date.now() + pendingNearLifetime,
        }),
      );
    }
  }

  function restore(params) {
    queued = undefined;
    const raw = window.sessionStorage.getItem(storageKey);
    if (raw) {
      window.sessionStorage.removeItem(storageKey);
      let pending;
      try {
        pending = JSON.parse(raw);
      } catch {
        // Corrupt navigation metadata is not a usable intent.
      }
      if (
        pending?.version === 1 &&
        Number.isFinite(pending.expires) &&
        pending.expires > Date.now() &&
        pending.expires <= Date.now() + pendingNearLifetime &&
        pending.destination === window.location.href &&
        pending.near === nearIdentity(params)
      ) {
        writeEntry(params, true);
      }
    }
    return entry(params) ? { transientLocation: true } : undefined;
  }

  function queue(params, metadata) {
    if (metadata?.transientLocation !== true) {
      window.sessionStorage.removeItem(storageKey);
    }
    if (typeof metadata?.transientLocation === "boolean") {
      queued = { near: nearIdentity(params), transient: metadata.transientLocation };
    }
  }

  function prepare(params) {
    const near = nearIdentity(params);
    const transient = queued && queued.near === near ? queued.transient : Boolean(entry(params));
    queued = undefined;
    const inheritedState = window.history.state;
    return () => writeEntry(params, transient, inheritedState);
  }

  return Object.freeze({ stage, restore, queue, prepare });
}

const inputs = new WeakMap();

function setRouteSurfaceVisible(surface, visible) {
  if (!surface) {
    return;
  }
  surface.toggleAttribute("hidden", !visible);
  surface.toggleAttribute("inert", !visible);
}

/** One store subscription controls the server-rendered landing and results surfaces. */
function bindRouteSurfaces(host, store, actions) {
  const surfaces = actions.routeSurfaces;
  if (!surfaces) {
    return () => {};
  }
  const owned = Object.values(surfaces).filter(Boolean);
  if (owned.some((surface) => resolveFinder(surface).element !== host)) {
    return () => {};
  }
  let previous;
  let previousDraft;
  const subscription = store.subscribe(({ route, landingDraft }) => {
    if (landingDraft !== previousDraft) {
      previousDraft = landingDraft;
      actions.landingDraftChanged?.();
    }
    setRouteSurfaceVisible(surfaces.landing, route === "landing");
    setRouteSurfaceVisible(surfaces.results, route === "results");
    if (route && route !== previous) {
      previous = route;
      actions.consumeRoute?.(route);
    }
  });
  return () => {
    subscription.disconnect();
    owned.forEach((surface) => setRouteSurfaceVisible(surface, false));
  };
}

/** Installs the private publication input for one mounted wrapper. */
export function registerFinderInput(host, input) {
  inputs.set(host, input);

  return function unregister() {
    if (inputs.get(host) === input) {
      inputs.delete(host);
    }
  };
}

/** Keeps the temporary Backbone action registry out of the finder store. */
export function createLegacyAdapter() {
  let store;
  let actions;
  let queuedRelease;
  let host;
  let stopRouteSurfaces;

  function forwardResultCommand(name) {
    return function runLegacyResultCommand(...args) {
      return actions?.[name]?.(...args);
    };
  }

  function runQueuedRelease() {
    if (queuedRelease === undefined || !actions) {
      return;
    }
    const options = queuedRelease;
    queuedRelease = undefined;
    actions.releaseFetching?.(options);
  }

  function setActions(next) {
    if (store.closed) {
      return;
    }
    actions = next;
    stopRouteSurfaces?.();
    stopRouteSurfaces = bindRouteSurfaces(host, store, actions);
    store.reportAttachment(true);
    runQueuedRelease();
  }

  function clearActions(owned) {
    if (store.closed || actions !== owned) {
      return;
    }
    actions = undefined;
    stopRouteSurfaces?.();
    stopRouteSurfaces = undefined;
    store.reportAttachment(false);
  }

  function queueRelease(options) {
    if (store.closed || options === undefined) {
      return;
    }
    queuedRelease = options;
    runQueuedRelease();
  }

  const commands = {
    search(value, options) {
      actions?.search?.(value, options);
    },
    navigate(destination) {
      return actions?.navigate(destination);
    },
    submitLandingSearch(...args) {
      return actions?.submitLandingSearch(...args);
    },
    releaseFetching(options) {
      actions?.releaseFetching?.(options);
    },
    applyRequiredFilters(selection) {
      actions?.applyRequiredFilters?.(selection);
    },
    persistSettings() {
      return actions?.persistSettings?.();
    },
    requestParams(name, input) {
      return actions?.[`${name}Params`]?.(input);
    },
    ...Object.fromEntries([...RESULT_COMMANDS, ...MAP_COMMANDS].map((name) => [name, forwardResultCommand(name)])),
  };

  function register(element, finder) {
    host = element;
    store = finder;
    const unregister = registerFinderInput(host, {
      search: commands.search,
      publishSearch: store.publishSearch,
      reportRequest: store.reportRequest,
      reportCompletion: store.reportCompletion,
      reportError: store.reportError,
      reportView: store.reportView,
      reportMapSelection: store.reportMapSelection,
      reportMapBoundsMode: store.reportMapBoundsMode,
      reportRoute: store.reportRoute,
      reportSettings: store.reportSettings,
      updateLandingDraft: store.updateLandingDraft,
      get landingDraft() {
        return store.state.value.landingDraft;
      },
      get landingDraftOptions() {
        return { transientLocation: Boolean(store.state.value.landingDraftTransientLocation) };
      },
      get route() {
        return store.state.value.route;
      },
      setActions,
      clearActions,
      get closed() {
        return store.closed;
      },
    });
    return function dispose() {
      unregister();
      stopRouteSurfaces?.();
      stopRouteSurfaces = undefined;
      actions?.dispose?.();
      actions = undefined;
      queuedRelease = undefined;
    };
  }

  return {
    register,
    queueRelease,
    commands,
  };
}

/** Connects a legacy writer, buffering its current search, request state and actions until mount. */
export function connectLegacyFinder(host, { signal } = {}) {
  let latest = {};
  let input;
  let closed = false;
  let pendingSearch;
  const connection = createElementConnection({
    resolve: () => resolveFinder(host),
    connect: connectInput,
  });

  function connectInput(element) {
    const current = inputs.get(element);

    if (!current) {
      return;
    }

    input = current;

    replay();

    return {
      get closed() {
        return current.closed;
      },

      disconnect() {
        if (current.landingDraft) {
          latest.landingDraft = cloneFinderData(current.landingDraft);
          latest.landingDraftOptions = cloneFinderData(current.landingDraftOptions);
        }
        if (latest.actions) {
          current.clearActions(latest.actions);
        }

        input = undefined;
      },
    };
  }

  const stop = observeHostConnections(host, connection.reconcile);

  function replay() {
    if (latest.landingDraft) {
      input.updateLandingDraft(latest.landingDraft, latest.landingDraftOptions);
    }
    if (latest.settings) {
      input.reportSettings(latest.settings);
    }
    if (latest.route) {
      input.reportRoute(latest.route);
    }
    if (latest.search) {
      input.publishSearch(latest.search.params, latest.search.options);
    }
    if (latest.completion) {
      input.reportRequest(latest.completion.request);
      input.reportCompletion(latest.completion.result);
    }
    if (latest.request && latest.request.generation !== latest.completion?.result.generation) {
      input.reportRequest(latest.request);
    }
    if (latest.error) {
      input.reportError(latest.error);
    }
    if (latest.view) {
      input.reportView(latest.view);
    }
    if (Object.hasOwn(latest, "mapSelection")) {
      input.reportMapSelection(latest.mapSelection);
    }
    if (latest.mapBoundsMode) {
      input.reportMapBoundsMode(latest.mapBoundsMode);
    }
    if (latest.actions) {
      input.setActions(latest.actions);
    }
    if (pendingSearch) {
      const command = pendingSearch;
      pendingSearch = undefined;
      input.search(...command);
    }
  }

  function search(value, options) {
    if (closed) {
      return;
    }
    pendingSearch = [value, options];
    const previous = input;
    connection.reconcile();
    if (input && input === previous) {
      pendingSearch = undefined;
      input.search(value, options);
    }
  }

  function reportRoute(route) {
    if (closed) {
      return;
    }
    latest.route = route;
    const previous = input;
    connection.reconcile();
    if (input && input === previous) {
      input.reportRoute(route);
    }
  }

  function updateLandingDraft(patch, options) {
    if (closed) {
      return;
    }
    latest.landingDraft = { ...latest.landingDraft, ...cloneFinderData(patch) };
    if (options) {
      latest.landingDraftOptions = cloneFinderData(options);
    }
    const previous = input;
    connection.reconcile();
    if (input && input === previous) {
      input.updateLandingDraft(patch, options);
    }
  }

  function reportSettings(settings) {
    if (closed) {
      return;
    }
    latest.settings = cloneFinderData(settings);
    const previous = input;
    connection.reconcile();
    if (input && input === previous) {
      input.reportSettings(latest.settings);
    }
  }

  function publishSearch(params, { filterValues } = {}) {
    if (closed) {
      return;
    }

    latest.search = cloneFinderData({ params, options: { filterValues } });

    const previous = input;

    connection.reconcile();

    if (input && input === previous) {
      input.publishSearch(latest.search.params, latest.search.options);
    }
  }

  function reportRequest(request) {
    if (closed) {
      return;
    }

    latest.request = cloneFinderData(request);
    latest.error = undefined;
    const previous = input;
    connection.reconcile();
    if (input && input === previous) {
      input.reportRequest(latest.request);
    }
  }

  function reportCompletion(completion) {
    if (closed || completion.generation !== latest.request?.generation) {
      return;
    }

    latest.completion = { request: latest.request, result: cloneFinderData(completion) };
    latest.view = undefined;
    latest.error = undefined;
    const previous = input;
    connection.reconcile();
    if (input && input === previous) {
      input.reportCompletion(latest.completion.result);
    }
  }

  function reportView(view) {
    if (closed || !latest.completion) {
      return;
    }

    latest.view = view;
    const previous = input;
    connection.reconcile();
    if (input && input === previous) {
      input.reportView(view);
    }
  }

  function reportMapSelection(resultGUID) {
    if (closed) {
      return;
    }
    latest.mapSelection = resultGUID ?? null;
    const previous = input;
    connection.reconcile();
    if (input && input === previous) {
      input.reportMapSelection(latest.mapSelection);
    }
  }

  function reportMapBoundsMode(mode) {
    if (closed || !["auto", "manual"].includes(mode)) {
      return;
    }
    latest.mapBoundsMode = mode;
    const previous = input;
    connection.reconcile();
    if (input && input === previous) {
      input.reportMapBoundsMode(mode);
    }
  }

  function reportError(error) {
    if (closed || error.generation !== latest.request?.generation) {
      return;
    }

    latest.error = cloneFinderData(error);
    const previous = input;
    connection.reconcile();
    if (input && input === previous) {
      input.reportError(latest.error);
    }
  }

  function setActions(actions) {
    if (closed) {
      return;
    }

    latest.actions = actions;
    const previous = input;
    connection.reconcile();
    if (input && input === previous) {
      input.setActions(actions);
    }
  }

  function disconnect() {
    if (closed) {
      return;
    }

    closed = true;
    if (latest.actions) {
      input?.clearActions(latest.actions);
    }
    latest = {};
    pendingSearch = undefined;
    stop();
    connection.disconnect();
    signal?.removeEventListener("abort", disconnect);
  }

  signal?.addEventListener("abort", disconnect, {
    once: true,
  });

  if (signal?.aborted) {
    disconnect();
  } else {
    connection.reconcile();
  }

  return {
    search,
    publishSearch,
    reportRoute,
    reportSettings,
    updateLandingDraft,
    get landingDraft() {
      return input?.landingDraft ?? latest.landingDraft;
    },
    get route() {
      return input?.route;
    },
    reportRequest,
    reportCompletion,
    reportMapSelection,
    reportMapBoundsMode,
    reportError,
    reportView,
    setActions,
    disconnect,
    get closed() {
      return closed;
    },
  };
}
